import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Lightbulb, TrendingUp, Target } from 'lucide-react'
import api from '@/services/api'

interface SegmentSuggestion {
  segmento: string
  totalLeads: number
  converted: number
  conversionRate: number
  topLeads: { id: string; empresaNome: string | null; score: number | null; temperatura: string | null; status: string }[]
}

export function BdrAutoProspection({ bdrId }: { bdrId: string }) {
  const [suggestions, setSuggestions] = useState<SegmentSuggestion[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!bdrId) return
    async function load() {
      setLoading(true)
      try {
        const { data } = await api.get('/bdr/auto-prospection', { params: { bdrId } })
        setSuggestions(data.data ?? [])
      } catch {
        // silent
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [bdrId])

  if (loading) {
    return (
      <div className="glass-card border-white/[0.06] p-5 space-y-3 animate-pulse">
        <div className="h-4 bg-white/[0.06] rounded w-48" />
        {[1, 2, 3].map((i) => <div key={i} className="h-16 bg-white/[0.04] rounded-lg" />)}
      </div>
    )
  }

  if (suggestions.length === 0) {
    return (
      <div className="glass-card border-white/[0.06] p-5 text-center">
        <Lightbulb size={24} className="mx-auto text-text-muted mb-2" />
        <p className="text-xs text-text-muted">Sem dados suficientes para sugerir segmentos</p>
      </div>
    )
  }

  return (
    <div className="glass-card border-white/[0.06] p-5">
      <div className="flex items-center gap-2 text-text-primary font-extrabold text-xs uppercase tracking-widest mb-5">
        <Lightbulb size={15} className="text-amber-400 shrink-0" />
        Sugestões de Prospecção por Segmento
      </div>

      <div className="space-y-3">
        {suggestions.map((seg, idx) => (
          <motion.div
            key={seg.segmento}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: idx * 0.05 }}
            className="p-4 bg-white/[0.02] border border-white/[0.04] rounded-xl"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Target size={12} className="text-accent" />
                <span className="text-sm font-bold text-text-primary">{seg.segmento}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-[10px] text-text-muted">{seg.totalLeads} leads</span>
                <div className="flex items-center gap-1">
                  <TrendingUp size={11} className={seg.conversionRate >= 20 ? 'text-emerald-400' : 'text-amber-400'} />
                  <span className={`text-xs font-extrabold ${seg.conversionRate >= 20 ? 'text-emerald-400' : 'text-amber-400'}`}>
                    {seg.conversionRate}%
                  </span>
                </div>
              </div>
            </div>

            {/* Conversion bar */}
            <div className="h-1.5 bg-white/[0.04] rounded-full overflow-hidden mb-3">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${Math.min(seg.conversionRate, 100)}%`,
                  backgroundColor: seg.conversionRate >= 20 ? '#10B981' : '#F59E0B',
                }}
              />
            </div>

            {/* Top leads */}
            {seg.topLeads.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {seg.topLeads.slice(0, 3).map((lead) => (
                  <span
                    key={lead.id}
                    className="text-[9px] font-bold px-2 py-0.5 bg-white/[0.03] border border-white/[0.06] rounded-md text-text-muted"
                  >
                    {lead.empresaNome || 'Sem nome'}
                    {lead.score ? ` · ${lead.score}` : ''}
                  </span>
                ))}
              </div>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  )
}
